import { useRef } from "react";

import toArray from "lodash/toArray";

import { useRouter } from "next/router";

import { useTranslator } from "@eo-locale/react";

import { SyntheticEvent, useEffect, useState } from "react";

import { Box, Button, Flex, Stack, Text } from "@/storybook-components";
import ProgressBar from "./progress-bar";
import QuestionList from "./question-list";
import { scrollIntoView } from "@/lib";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  paginateBackwards,
  removeSurveyErrors
} from "@/redux/features/survey/slice";
import { saveSurvey } from "@/redux/features/survey/thunk";
import RequiredField from "../questions/required-field";
import Alerts from "../alerts";
import useUser from "@/hooks/useUser";
import InvisibleReCaptcha from "../recaptcha/invisible";
import useLogout from "@/hooks/useLogout";
import usePinned from "@/hooks/usePinned";
import useEmbeds from "@/hooks/useEmbeds";
import googleTranslateInit from "@/helpers/google-translate-init";

const SurveyForm = () => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { query } = useRouter();
  const recaptchaRef = useRef(null);
  const {
    errors,
    answers,
    survey,
    pages,
    questions,
    options,
    saving,
    saveError,
    taken,
    viewResponse
  } = useAppSelector((state) => state.survey);
  const { isRegisteredUserLoggedIn, isUserAdmin, isPreviewUser } = useUser();
  const { profile } = useAppSelector((state) => state.user.registeredUser);
  const { login, roleName } = profile;
  const { logOut } = useLogout();

  const { settings } = useAppSelector((state) => state.settings);
  const { recaptchaSignupEnabled, googleTranslationEnabled } = settings;
  const [currentPageHasRequiredQuestion, setCurrentPageHasRequiredQuestion] =
    useState(false);
  const { isInsideIframe, isEmbed } = useEmbeds();
  const { isPinned } = usePinned();
  const { canContribute, visibilityMode } = useAppSelector(
    (state) => state.project.project
  );

  const {
    state,
    showProgressBar,
    allowUserToPostMultipleTimes,
    allowAnonymousParticipation,
    allowUnverifiedParticipation,
    multipleTimesMsg
  } = survey;
  const currentPage = pages.find((page) => page.current);
  let isReCaptchaEnabled = false;
  // It could be the end of survey in the following scenarios:
  // 1. It's the last page
  // 2. Page triggers the end of survey
  // 3. If page skip doesn't exist & a question skips to the end of survey

  let endOfSurvey = false;
  const questionsInPage = toArray(questions).filter(
    (q) => q.pageId === currentPage?.id
  );
  for (let i = 0; i < questionsInPage.length; i++) {
    const question = questionsInPage[i];
    if (
      (question.type === "RadioButtonQuestion" ||
        question.type === "DropDownQuestion") &&
      !!answers[question.id]
    ) {
      const option = toArray(options).filter(
        (o) => o.id === answers[question.id].id
      );
      if (option[0]?.triggerEndOfSurvey) {
        endOfSurvey = true;
        break;
      }
    }
  }

  const [
    showAllowUserToPostMultipleTimes,
    setShowAllowUserToPostMultipleTimes
  ] = useState(
    taken &&
      !isUserAdmin &&
      allowUserToPostMultipleTimes &&
      !allowAnonymousParticipation &&
      !allowUnverifiedParticipation
  );

  // find out if a page contains any mandatory questions
  useEffect(() => {
    if (
      questionsInPage.filter((question) => question.required && !question?.skip)
        .length > 0
    ) {
      setCurrentPageHasRequiredQuestion(true);
    } else {
      setCurrentPageHasRequiredQuestion(false);
    }
  }, [questionsInPage]);

  const handlePrevious = async () => {
    // need to trigger Google Translate upon changing the pages of multi-page survey
    googleTranslateInit(isEmbed, googleTranslationEnabled);
    if (Object.keys(errors).length) {
      questionsInPage.forEach((question) => {
        if (errors[question.id]) {
          dispatch(
            removeSurveyErrors({
              questionId: question.id
            })
          );
        }
      });
    }

    dispatch(paginateBackwards({ pageId: currentPage.previous }));

    const formField: HTMLElement = document.querySelector(
      ".ehq-surveyContainer form"
    );
    formField?.focus();

    return scrollIntoView(".ehq-surveyContainer");
  };

  const postSurveyResponse = async (e: SyntheticEvent) => {
    e.preventDefault();

    await dispatch(saveSurvey({ query, recaptchaRef, isReCaptchaEnabled }));

    // need to trigger Google Translate upon changing the pages of multi-page survey
    googleTranslateInit(isEmbed, googleTranslationEnabled);
    if (document.querySelector(".ehq-question-error")) {
      const errorField: HTMLElement =
        document.querySelector(
          ".ehq-question-error button, .ehq-question-error input, .ehq-question-error textarea"
        ) ?? document.querySelector(".ehq-question-error label");

      errorField?.focus();
      return scrollIntoView(".ehq-question-error");
    }

    const formField: HTMLElement = document.querySelector(
      ".ehq-surveyContainer form"
    );
    formField?.focus();

    return scrollIntoView(".ehq-surveyContainer");
  };

  const renderFormButtons = () => {
    const isEndOfSurvey =
      currentPage.last || currentPage.triggerEndOfSurvey || endOfSurvey;

    return (
      <Flex
        mt={3}
        align="center"
        justifyContent={currentPage.first ? "flex-end" : "space-between"}
      >
        {!currentPage.first && (
          <Button
            fontFamily="inherit"
            variant="primary"
            isDisabled={saving}
            onClick={handlePrevious}
            className="ehq-surveyPreviousButton"
            size={{ base: "lg", lg: "md" }}
          >
            {translate("common.previous")}
          </Button>
        )}

        <Button
          type="submit"
          isLoading={saving}
          fontFamily="inherit"
          variant="primary"
          className="ehq-surveySubmitButton"
          size={{ base: "lg", lg: "md" }}
        >
          {isEndOfSurvey
            ? translate("common.submit")
            : !isRegisteredUserLoggedIn || (isUserAdmin && state === "draft")
              ? translate("common.next")
              : translate("common.saveAndContinue")}
        </Button>
      </Flex>
    );
  };
  const loggedInUserNode = () => {
    if (!login || viewResponse || !currentPage?.first) return null;

    return (
      <Box>
        <Text
          fontSize="0.75rem"
          lineHeight="133%"
          fontWeight="normal"
          letterSpacing="0.2px"
          color="gray.600"
        >
          {translate("survey.submittingSurveyAs")} <b>{login}</b>,&nbsp;
          {translate("survey.toChange")}&nbsp;
          <Button
            onClick={() => logOut()}
            variant="link"
            data-testid="survey-logoutButton"
            fontSize="xs"
          >
            {translate("common.logout")}
          </Button>
          &nbsp;
        </Text>
      </Box>
    );
  };

  const renderRecaptcha = () => {
    const shouldShowRecaptcha = isRegisteredUserLoggedIn
      ? roleName === "participant"
        ? !!currentPage?.first
        : false
      : currentPage?.last || currentPage?.triggerEndOfSurvey || endOfSurvey;

    isReCaptchaEnabled =
      shouldShowRecaptcha &&
      recaptchaSignupEnabled &&
      !!process.env.RECAPTCHA_INVISIBLE_SITE_KEY;

    if (!isReCaptchaEnabled) return null;

    if (isInsideIframe || isPinned) {
      return (
        <Flex justifyContent="right">
          <InvisibleReCaptcha recaptchaRef={recaptchaRef} badge="inline" />
        </Flex>
      );
    }

    return <InvisibleReCaptcha recaptchaRef={recaptchaRef} />;
  };

  return (
    <Stack className="ehq-surveyFormContainer" spacing={12}>
      <Stack spacing={4}>
        {showAllowUserToPostMultipleTimes && (
          <Alerts
            status="info"
            description={
              multipleTimesMsg || translate("survey.alreadyTakenPartInSurvey")
            }
            onClose={() => setShowAllowUserToPostMultipleTimes(false)}
          />
        )}
        {loggedInUserNode()}
        {showProgressBar && <ProgressBar />}

        {currentPageHasRequiredQuestion && <RequiredField />}
      </Stack>

      {!currentPage ? null : (
        <Box
          as="form"
          noValidate
          tabIndex={-1}
          outline="none"
          onSubmit={postSurveyResponse}
          aria-label={translate("survey.takeSurvey")}
        >
          <Stack
            as="fieldset"
            spacing={12}
            disabled={
              visibilityMode === "Panelled" && (!canContribute || isPreviewUser)
            }
          >
            <QuestionList />
            {saveError && (
              <Alerts status="error" title={translate("survey.saveError")} />
            )}
            {renderFormButtons()}
            {renderRecaptcha()}
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default SurveyForm;
